// @components/CreateAuctionForm/validation.ts
import {
  array,
  boolean,
  date,
  mixed,
  number,
  object,
  string,
  ValidationError,
} from "yup";

import { ASSETS_FAMILY } from "@utils";
import {
  MAX_CONVERSION_RATE_VALUE,
  MAX_DEPOSIT_VALUE,
  MAX_FEE_VALUE,
  MIN_DEPOSIT_VALUE,
  MIN_FEE_VALUE,
  MIN_NON_WFP_LOTS_VALUE,
  MIN_UNHCR_SERVICES_VALUE,
} from "./consts";
import { TCreateAuctionFormValues } from "./CreateAuctionForm";

const MAX_50MB = 50 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [".pdf", ".doc", ".docx", ".xls", ".xlsx", ".txt"];

const isAllowedType = (file?: File | null) => {
  if (!file) return true; // Allow null/undefined files
  const fileName = file.name.toLowerCase();
  return ALLOWED_FILE_TYPES.some((ext) => fileName.endsWith(ext));
};

const personalDataSchema = object({
  fullName: string(),
  email: string().email("errors.email.invalid"),
});

export const VALIDATION_SCHEMA = object({
  isOnline: boolean(),
  country: number().required("errors.country.required"),
  city: number().required("errors.city.required"),
  date: date()
    .when("isOnline", {
      is: true,
      then: date().min(new Date(), "errors.date.later"),
    })
    .required("errors.date.required"),
  auctionEndDate: date()
    .min(new Date(), "errors.date.later")
    .when("isOnline", {
      is: true,
      then: date().required("errors.date.required"),
    }),
  stakeholders: array().of(
    object({
      country_geoname_id: number(),
      agency: string(),
    })
  ),
  currency: string().required("errors.currency.required"),
  conversionRate: number()
    .positive("auctionForm.errors.conversionRate.min")
    .max(MAX_CONVERSION_RATE_VALUE, "auctionForm.errors.conversionRate.max")
    .test(
      "maxDigitsAfterDecimal",
      "auctionForm.errors.conversionRate.decimals",
      (number) =>
        number ? /^\d+(\.\d{1,3})?$/.test(number?.toString() ?? "") : true
    ),
  auctioneerFees: array()
    .required("auctionForm.errors.fee.required")
    .of(
      object({
        assets: array().of(string()),
        fee: number()
          .min(MIN_FEE_VALUE, "auctionForm.errors.fee.min")
          .max(MAX_FEE_VALUE, "auctionForm.errors.fee.max"),
      })
    ),
  deposits: array()
    .required("auctionForm.errors.deposit.required")
    .of(
      object({
        assets: array().of(string()),
        deposit: number()
          .min(MIN_DEPOSIT_VALUE, "auctionForm.errors.deposit.min")
          .max(MAX_DEPOSIT_VALUE, "auctionForm.errors.deposit.max"),
      })
    ),
  unhcrServicesFee: number()
    .min(MIN_UNHCR_SERVICES_VALUE, "auctionForm.errors.unhcrServicesFee.min")
    .test(
      "maxDigitsAfterDecimal",
      "auctionForm.errors.unhcrServicesFee.decimals",
      (number) =>
        number ? /^\d+(\.\d{1,2})?$/.test(number?.toString() ?? "") : true
    ),
  nonWfpLots: number()
    .positive("auctionForm.errors.nonWfpLots.min")
    .min(MIN_NON_WFP_LOTS_VALUE, "auctionForm.errors.nonWfpLots.min"),
  auctioneerInvoice: number()
    .min(MIN_UNHCR_SERVICES_VALUE, "auctionForm.errors.auctioneerInvoice.min")
    .test(
      "maxDigitsAfterDecimal",
      "auctionForm.errors.auctioneerInvoice.decimals",
      (number) =>
        number ? /^\d+(\.\d{1,2})?$/.test(number?.toString() ?? "") : true
    ),
  supervisor: personalDataSchema,
  focalPoint: personalDataSchema,
  representative: personalDataSchema,
  emailLanguage: string().when("isOnline", {
    is: true,
    then: string().required("auctionForm.errors.emailLanguage.required"),
  }),
  customDeposit: boolean(),
  customDepositName: string().when("customDeposit", {
    is: true,
    then: (s) =>
      s.trim().required("auctionForm.errors.customDepositName.required"),
    otherwise: (s) => s.notRequired(),
  }),

  depositAttachment: mixed<File | null>().when("customDeposit", {
    is: true,
    then: (m) =>
      m
        .required("auctionForm.errors.depositAttachment.required")
        .test(
          "file-type",
          "auctionForm.errors.file.format", // or your dedicated key if you have one
          (file) => isAllowedType(file as File | null)
        )
        .test(
          "file-size",
          "auctionForm.errors.file.max50mb", // updated size error
          (file) => !file || (file as File).size <= MAX_50MB
        ),
    otherwise: (m) => m.nullable().notRequired(),
  }),
});

export const validateForm = (values: TCreateAuctionFormValues) => {
  const errors: any = {};

  if (!values.isOnline) {
    if (
      values.auctioneerFees.flatMap((auctionGroup) => auctionGroup.assets)
        .length !== ASSETS_FAMILY.length
    ) {
      errors.auctioneerFees = "auctionForm.errors.fee.invalid";
    }
    if (
      values.deposits.flatMap((depositGroup) => depositGroup.assets).length !==
      ASSETS_FAMILY.length
    ) {
      errors.deposit = "auctionForm.errors.deposit.invalid";
    }
  }

  if (values.isOnline && values.requiresOnlineDeposits) {
    if (
      values.deposits.flatMap((depositGroup) => depositGroup.assets).length !==
      ASSETS_FAMILY.length
    ) {
      errors.deposit = "auctionForm.errors.deposit.invalid";
    }
  }

  const enforceAllOrNothing = (
    key: "supervisor" | "focalPoint" | "representative"
  ) => {
    const node = values[key];
    if (node) {
      const { fullName, email, phone } = node as any;
      const fieldsFilled = [fullName, email, phone].filter(Boolean).length;
      if (fieldsFilled > 0 && fieldsFilled < 3) {
        errors[key] = errors[key] || {};
        if (!fullName) {
          errors[key].fullName =
            "auctionForm.errors.supervisor.fullNameRequired";
        }
        if (!email) {
          errors[key].email = "auctionForm.errors.supervisor.emailRequired";
        }
        if (!phone) {
          errors[key].phone = "auctionForm.errors.supervisor.phoneRequired";
        }
      }
    }
  };

  enforceAllOrNothing("supervisor");
  enforceAllOrNothing("focalPoint");
  enforceAllOrNothing("representative");

  return errors;
};

/**
 * Runs Yup (abortEarly=false) + custom validateForm,
 * logs a readable table of failures, and returns nested errors for Formik.
 */
export const validateAndExplain = async (values: TCreateAuctionFormValues) => {
  const flat: Record<string, any> = {};

  // 1) Collect all Yup errors
  try {
    await VALIDATION_SCHEMA.validate(values, { abortEarly: false });
  } catch (e) {
    const err = e as ValidationError;
    if (err.inner?.length) {
      for (const item of err.inner) {
        if (!item.path) continue;
        const prev = flat[item.path];
        flat[item.path] = prev
          ? Array.from(
              new Set([...(Array.isArray(prev) ? prev : [prev]), item.message])
            )
          : item.message;
      }
    } else if (err.path) {
      flat[err.path] = err.message;
    }
  }

  // 2) Merge custom errors (deep -> flat)
  const custom = validateForm(values);
  const mergeDeepAsFlat = (obj: any, prefix = "") => {
    Object.entries(obj || {}).forEach(([k, v]) => {
      const path = prefix ? `${prefix}.${k}` : k;
      if (v && typeof v === "object" && !Array.isArray(v)) {
        mergeDeepAsFlat(v, path);
      } else {
        const prev = flat[path];
        flat[path] = prev
          ? Array.from(new Set([...(Array.isArray(prev) ? prev : [prev]), v]))
          : v;
      }
    });
  };
  mergeDeepAsFlat(custom);

  // 3) Pretty-print once for quick debugging
  if (Object.keys(flat).length) {
    const rows = Object.entries(flat).map(([path, msg]) => ({
      path,
      message: Array.isArray(msg) ? msg.join(" | ") : msg,
    }));
    // eslint-disable-next-line no-console
    console.groupCollapsed(
      "%c❌ Form invalid — reasons",
      "color:#b00020;font-weight:bold"
    );
    // eslint-disable-next-line no-console
    console.table(rows);
    // eslint-disable-next-line no-console
    console.log("Raw merged errors (flat):", flat);
    // eslint-disable-next-line no-console
    console.groupEnd();
  }

  // 4) Convert flat "a.b.c" keys into nested objects for Formik
  const toNested = (flatObj: Record<string, any>) => {
    const root: Record<string, any> = {};
    for (const [path, val] of Object.entries(flatObj)) {
      const keys = path.split(".");
      let cur = root;
      keys.forEach((k, i) => {
        if (i === keys.length - 1) {
          cur[k] = val;
        } else {
          cur[k] = cur[k] || {};
          cur = cur[k];
        }
      });
    }
    return root;
  };

  return toNested(flat);
};
